/**
 * dcent-recommended-products.js — drop-04 §19 "추천 제품" 캐러셀 컨트롤.
 * 디자인 SoT: ~/work/dcent-design-drops/drop-04-20260806/src/components/RecommendedProducts.tsx
 *
 * 하는 일은 셋뿐이다.
 *   1) 화살표·인디케이터 클릭 → **인덱스 기준 절대 위치**로 이동
 *   2) 스크롤 위치로 화살표 비활성·가장자리 페이드·활성 인디케이터 갱신
 *   3) 크기 변화(리사이즈·회전·이미지 로드) 추적
 *
 * 왜 상대 이동(scrollBy)이 아닌가: 끝에서 스크롤이 클램프되면 그만큼 어긋남이 남고,
 * 그게 누적되면 카드가 반쯤 걸친 위치에서 멈춘다. 인덱스로 목표 위치를 다시 계산하면 안 쌓인다.
 *
 * 담기 동작은 여기 없다 — 카드의 담기 폼은 공유 스니펫(dcent-add-to-cart)이 갖고,
 * PDP 히어로·고정 바의 담기는 dcent-pdp-buy.js 가 전담한다(계약 §3).
 *
 * 선택 상태는 class 가 아니라 aria-current 로만 표현하고, 보이는 상태는 CSS 가 만든다.
 */

const EDGE = 8; // 끝 판정 여유(px) — 서브픽셀 스크롤 잔차 흡수

function setupCarousel(section) {
  const track = section.querySelector("[data-dcent-rp-track]");
  if (!track || track.dataset.dcentRpReady === "true") return;
  track.dataset.dcentRpReady = "true";

  const prevs = section.querySelectorAll("[data-dcent-rp-prev]");
  const nexts = section.querySelectorAll("[data-dcent-rp-next]");
  const dots = section.querySelectorAll("[data-dcent-rp-dot]");
  const fadeLeft = section.querySelector("[data-dcent-rp-fade-left]");
  const fadeRight = section.querySelector("[data-dcent-rp-fade-right]");

  const cardCount = () => track.children.length;

  // 한 칸 이동 거리. 카드 두 장이 있으면 실측 간격을 그대로 쓰고(gap 값 추측 불필요),
  // 한 장뿐이면 카드 폭 + gap 으로 근사한다.
  const step = () => {
    const first = track.children[0];
    const second = track.children[1];
    if (first && second) {
      const measured = second.offsetLeft - first.offsetLeft;
      if (measured > 0) return measured;
    }
    return first ? first.offsetWidth + 16 : 456;
  };

  const goTo = (index) => {
    const last = cardCount() - 1;
    if (last < 0) return;
    const target = Math.max(0, Math.min(last, index));
    track.scrollTo({ left: target * step(), behavior: "smooth" });
  };

  const go = (dir) => goTo(Math.round(track.scrollLeft / step()) + dir);

  const update = () => {
    const max = track.scrollWidth - track.clientWidth;
    const moved = track.scrollLeft > EDGE;
    const atStart = track.scrollLeft <= EDGE;
    // 마운트 직후에는 스크롤 최대값이 0 으로 측정되는 프레임이 있다. 그때 끝으로 판정하면
    // 양쪽 화살표가 동시에 비활성이 된다 — max 가 실제로 잡혔을 때만 끝으로 본다.
    // 카드가 한 장뿐이라 정말로 스크롤할 수 없는 경우만 예외로 끝 취급한다.
    const atEnd =
      max > EDGE ? track.scrollLeft >= max - EDGE : cardCount() <= 1;

    prevs.forEach((btn) => {
      btn.disabled = atStart;
    });
    nexts.forEach((btn) => {
      btn.disabled = atEnd;
    });

    if (fadeLeft) fadeLeft.dataset.on = moved ? "true" : "false";
    if (fadeRight) fadeRight.dataset.on = moved && !atEnd ? "true" : "false";

    const active = Math.max(
      0,
      Math.min(cardCount() - 1, Math.round(track.scrollLeft / step())),
    );
    dots.forEach((dot, i) => {
      // 렌더된 카드보다 점이 많으면(핸들 미해석 등) 남는 점은 감춘다.
      dot.hidden = i >= cardCount();
      dot.setAttribute("aria-current", i === active ? "true" : "false");
    });
  };

  prevs.forEach((btn) => btn.addEventListener("click", () => go(-1)));
  nexts.forEach((btn) => btn.addEventListener("click", () => go(1)));
  dots.forEach((dot) =>
    dot.addEventListener("click", () =>
      goTo(Number(dot.dataset.dcentRpDot) || 0),
    ),
  );

  update();
  // 마운트 직후 레이아웃이 아직 안 잡힌 프레임 대비 — 다음 프레임에 한 번 더 계산한다.
  requestAnimationFrame(update);

  track.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update);

  if (typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver(update);
    ro.observe(track);
    if (track.children[0]) ro.observe(track.children[0]);
  }
}

export function initRecommendedProducts(root = document) {
  root.querySelectorAll("[data-dcent-recommended]").forEach(setupCarousel);
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () =>
      initRecommendedProducts(),
    );
  } else {
    initRecommendedProducts();
  }
  // 테마 편집기가 섹션 DOM 을 교체할 때 재초기화(라이브 스토어에서는 발생하지 않는다).
  document.addEventListener("shopify:section:load", (e) =>
    initRecommendedProducts(e.target),
  );
}
